interface PendingLoad<K, V> {
  key: K;
  resolve: (value: V) => void;
  reject: (error: unknown) => void;
}

/**
 * Collects the keys requested within a short window and fetches them in one call. React Query schedules retries
 * on separate timers, so the window has to be wide enough to catch them too.
 */
export class RequestBatcher<K, V> {
  private readonly fetchMany: (keys: K[]) => Promise<V[]>;
  private readonly windowMs: number;
  private pendingLoads: PendingLoad<K, V>[] = [];

  /** @param fetchMany - returns one value per key, in the order of the keys */
  constructor(fetchMany: (keys: K[]) => Promise<V[]>, windowMs = 10) {
    this.fetchMany = fetchMany;
    this.windowMs = windowMs;
  }

  public load(key: K): Promise<V> {
    return new Promise((resolve, reject) => {
      if (!this.pendingLoads.length) {
        setTimeout(() => this.flush(), this.windowMs);
      }

      this.pendingLoads.push({ key, resolve, reject });
    });
  }

  private async flush() {
    const loads = this.pendingLoads;
    this.pendingLoads = [];

    // A key requested twice within the window, e.g. by a query removed and recreated, is fetched once.
    const keys = [...new Set(loads.map((load) => load.key))];

    try {
      const values = await this.fetchMany(keys);

      loads.forEach((load) => load.resolve(values[keys.indexOf(load.key)] as V));
    } catch (error) {
      loads.forEach((load) => load.reject(error));
    }
  }
}
